import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { COMPARABLES_SETTINGS_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import {
  useAutoTour,
  useTourReveal,
} from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import {
  UPDATE_CMA_REPORT_COMPARABLES_SHOW_FULL_IDX_DESCRIPTION,
  UPDATE_CMA_REPORT_COMPARABLES_SQUARE_METER_CALCULATION,
  UPDATE_CMA_REPORT_COMPARABLE_LISTINGS_MAP_ZOOM_LEVEL,
  UPDATE_CMA_REPORT_INCLUDE_COMPARABLE_LISTING_DETAILS,
  UPDATE_CMA_REPORT_INCLUDE_RENTAL_COMPARABLE_LISTING_DETAILS,
  UPDATE_CMA_REPORT_SHOW_COMPARABLE_LISTING_CARDS,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import {
  UPDATE_RENTAL_COMPARABLE_LISTINGS_MAP_ZOOM_LEVEL,
  UPDATE_RENTAL_SHOW_COMPARABLE_LISTING_CARDS,
  UPDATE_RENTAL_SHOW_FULL_DESCRIPTION,
} from '@modules/reports/cma-reports/rentalQueries';
import ZoomLevelSlider from '@modules/reports/shared/ZoomLevelSlider';
import { useLocale } from '@shared/hooks/locale';

type ComparableListingsSettingsProps = {
  isPropertyComparableListingsSquareMeterCalculation: boolean;
  isShowFullIdxDescription?: boolean;
  includeComparableListingDetails: boolean;
  showListingCards: boolean;
  comparableListingsMapZoomLevel: number | null;
  showSquareMeterCalculation?: boolean;
  variant?: 'sale' | 'rental';
};

function ComparableListingsSettings({
  isPropertyComparableListingsSquareMeterCalculation,
  isShowFullIdxDescription = false,
  includeComparableListingDetails,
  showListingCards,
  comparableListingsMapZoomLevel,
  showSquareMeterCalculation = true,
  variant = 'sale',
}: ComparableListingsSettingsProps): React.ReactElement {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const isRental = variant === 'rental';
  // The cards tour explains the sale comparables' layout setting, inside
  // Settings: not the rentals' (they have their own tour).
  useTourReveal(
    COMPARABLES_SETTINGS_ANCHOR,
    () => setExpanded(true),
    !isRental,
  );
  useAutoTour('cma-comparables-cards', !isRental);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data: cmaReportData } = useReportContext();
  const displayedZoomLevel = comparableListingsMapZoomLevel ?? 14;

  const [
    updateSquareMeterCalculation,
    { loading: loadingSquareMeterCalculation },
  ] = useMutation(UPDATE_CMA_REPORT_COMPARABLES_SQUARE_METER_CALCULATION, {
    onError: setErrorLike,
  });

  const handleSquareMeterCalculationToggle = useCallback(
    async (value: boolean) => {
      await updateSquareMeterCalculation({
        variables: {
          id: cmaReportData?.id,
          is_property_comparable_listings_square_meter_calculation: value,
        },
      });
    },
    [cmaReportData?.id, updateSquareMeterCalculation],
  );

  const [
    updateHideFullIdxDescription,
    { loading: loadingHideFullIdxDescription },
  ] = useMutation(UPDATE_CMA_REPORT_COMPARABLES_SHOW_FULL_IDX_DESCRIPTION, {
    onError: setErrorLike,
  });

  const [
    updateShowFullRentalDescription,
    { loading: loadingShowFullRentalDescription },
  ] = useMutation(UPDATE_RENTAL_SHOW_FULL_DESCRIPTION, {
    onError: setErrorLike,
  });

  const [updateMapZoomLevel] = useMutation(
    UPDATE_CMA_REPORT_COMPARABLE_LISTINGS_MAP_ZOOM_LEVEL,
    { onError: setErrorLike },
  );

  const [updateRentalMapZoomLevel] = useMutation(
    UPDATE_RENTAL_COMPARABLE_LISTINGS_MAP_ZOOM_LEVEL,
    { onError: setErrorLike },
  );

  const handleHideFullIdxDescriptionToggle = useCallback(
    async (value: boolean) => {
      if (isRental) {
        await updateShowFullRentalDescription({
          variables: {
            id: cmaReportData?.id,
            show_full_rental_description: value,
          },
        });
        return;
      }
      await updateHideFullIdxDescription({
        variables: {
          id: cmaReportData?.id,
          show_full_idx_description: value,
        },
      });
    },
    [
      cmaReportData?.id,
      isRental,
      updateShowFullRentalDescription,
      updateHideFullIdxDescription,
    ],
  );

  const [
    updateIncludeComparableListingDetails,
    { loading: loadingIncludeComparableListingDetails },
  ] = useMutation(UPDATE_CMA_REPORT_INCLUDE_COMPARABLE_LISTING_DETAILS, {
    onError: setErrorLike,
  });

  const [
    updateIncludeRentalComparableListingDetails,
    { loading: loadingIncludeRentalComparableListingDetails },
  ] = useMutation(UPDATE_CMA_REPORT_INCLUDE_RENTAL_COMPARABLE_LISTING_DETAILS, {
    onError: setErrorLike,
  });

  const handleIncludeComparableListingDetailsToggle = useCallback(
    async (value: boolean) => {
      if (isRental) {
        await updateIncludeRentalComparableListingDetails({
          variables: {
            id: cmaReportData?.id,
            include_rental_comparable_listing_details: value,
          },
        });
        if (!value && isShowFullIdxDescription) {
          await updateShowFullRentalDescription({
            variables: {
              id: cmaReportData?.id,
              show_full_rental_description: false,
            },
          });
        }
        return;
      }
      await updateIncludeComparableListingDetails({
        variables: {
          id: cmaReportData?.id,
          include_comparable_listing_details: value,
        },
      });
      if (!value && isShowFullIdxDescription) {
        await updateHideFullIdxDescription({
          variables: {
            id: cmaReportData?.id,
            show_full_idx_description: false,
          },
        });
      }
    },
    [
      cmaReportData?.id,
      isRental,
      updateIncludeRentalComparableListingDetails,
      updateIncludeComparableListingDetails,
      isShowFullIdxDescription,
      updateShowFullRentalDescription,
      updateHideFullIdxDescription,
    ],
  );

  const [updateShowListingCards, { loading: loadingShowListingCards }] =
    useMutation(UPDATE_CMA_REPORT_SHOW_COMPARABLE_LISTING_CARDS, {
      onError: setErrorLike,
    });

  const [
    updateShowRentalListingCards,
    { loading: loadingShowRentalListingCards },
  ] = useMutation(UPDATE_RENTAL_SHOW_COMPARABLE_LISTING_CARDS, {
    onError: setErrorLike,
  });

  const handleShowListingCardsToggle = useCallback(
    async (value: boolean) => {
      if (isRental) {
        await updateShowRentalListingCards({
          variables: {
            id: cmaReportData?.id,
            show_rental_comparable_listing_cards: value,
          },
        });
        return;
      }
      await updateShowListingCards({
        variables: {
          id: cmaReportData?.id,
          show_comparable_listing_cards: value,
        },
      });
    },
    [
      cmaReportData?.id,
      isRental,
      updateShowRentalListingCards,
      updateShowListingCards,
    ],
  );

  const handleMapZoomLevelChange = useCallback(
    async (value: number) => {
      if (isRental) {
        await updateRentalMapZoomLevel({
          variables: {
            id: cmaReportData?.id,
            rental_comparable_listings_map_zoom_level: value,
          },
        });
        return;
      }
      await updateMapZoomLevel({
        variables: {
          id: cmaReportData?.id,
          comparable_listings_map_zoom_level: value,
        },
      });
    },
    [cmaReportData?.id, isRental, updateRentalMapZoomLevel, updateMapZoomLevel],
  );

  return (
    <>
      <Box
        {...tourAnchor(COMPARABLES_SETTINGS_ANCHOR)}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <SettingsIcon
              size={20}
              color={'var(--mui-palette-text-secondary)'}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('Settings')}
            </Typography>
            {!expanded && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {showSquareMeterCalculation &&
                  isPropertyComparableListingsSquareMeterCalculation &&
                  ` • ${t('Square meter')}`}
                {showListingCards && ` • ${t('Cards')}`}
                {isShowFullIdxDescription && ` • ${t('Show full description')}`}
                {includeComparableListingDetails && ` • ${t('Detail pages')}`}
                {` • ${t('Zoom')}: ${displayedZoomLevel}`}
              </Typography>
            )}
          </Stack>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUpIcon size={20} />
            ) : (
              <ChevronDownIcon size={20} />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Stack sx={{ p: 1, gap: 2 }}>
            {showSquareMeterCalculation && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isPropertyComparableListingsSquareMeterCalculation}
                    onChange={e =>
                      handleSquareMeterCalculationToggle(e.target.checked)
                    }
                    disabled={loadingSquareMeterCalculation}
                  />
                }
                label={t('Use square meter calculation')}
                sx={{ m: 0 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showListingCards}
                  onChange={e => handleShowListingCardsToggle(e.target.checked)}
                  disabled={
                    isRental
                      ? loadingShowRentalListingCards
                      : loadingShowListingCards
                  }
                />
              }
              label={t(
                'Show comparable listings as cards with photo and description',
              )}
              sx={{
                m: 0,
                '& span': {
                  whiteSpace: 'break-spaces',
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeComparableListingDetails}
                  onChange={e =>
                    handleIncludeComparableListingDetailsToggle(
                      e.target.checked,
                    )
                  }
                  disabled={
                    isRental
                      ? loadingIncludeRentalComparableListingDetails
                      : loadingIncludeComparableListingDetails
                  }
                />
              }
              label={t('Show comparable listings detail pages in the appendix')}
              sx={{
                m: 0,
                '& span': {
                  whiteSpace: 'break-spaces',
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isShowFullIdxDescription}
                  onChange={e =>
                    handleHideFullIdxDescriptionToggle(e.target.checked)
                  }
                  disabled={
                    (isRental
                      ? loadingShowFullRentalDescription
                      : loadingHideFullIdxDescription) ||
                    !includeComparableListingDetails
                  }
                />
              }
              label={t(
                'Show full description for comparable listing in the appendix',
              )}
              sx={{
                m: 0,
                '& span': {
                  whiteSpace: 'break-spaces',
                },
              }}
            />

            <ZoomLevelSlider
              value={comparableListingsMapZoomLevel}
              defaultValue={14}
              onChange={handleMapZoomLevelChange}
              label={t('Map zoom level')}
            />
          </Stack>
        </Collapse>
      </Box>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
}

export default ComparableListingsSettings;
